import { z } from 'zod';
import { useEffect } from 'react';
import { validate } from 'rut.js';
import { formatPercentage } from '../../../utils/formatters';
import { BaseSimulationParams } from '../../../interfaces/simulations';
import { simulationManager } from '../../../utils/simulationsManager';
import {
  validateAgeMayority,
  validateMaxAge,
  validateGoalAmountBySavingsOption,
  validateSavingsOption,
  validateTimeFrameForGoal,
} from '../../../utils/validations';
import { injectSimulationIdToInfograph } from '../../../behaviors/infograhps/handleInfographDownload';
import { SavingsOption } from '../../../utils/constants';
import { VALIDATION_MESSAGES } from '../../../utils/validationMessages';
import { EVENTS, tag } from '../../../behaviors/cms/gtm-inputs';
import { selectNumberOption } from '../../../utils/commonSchemaValidators';

const MIN_AMOUNT = 1;
const [MIN_RENTABILITY_RATE, MAX_RENTABILITY_RATE] = [1, 5];
const MIN_TIME_FRAME = 1;
const MAX_CONTRACT_AGE = 68;

export const fullFlexibleValidationSchema = (minimumDeathCapital: number) => z.object({
  rut: z.string()
    .refine((rut) => validate(rut), { message: VALIDATION_MESSAGES.INVALID_RUT }),
  date_of_birth: z
    .string()
    .min(1, 'Este campo es requerido')
    .refine((date_of_birth) => validateAgeMayority(date_of_birth), {
      message: 'El cliente debe ser mayor de edad',
      path: ['date_of_birth'],
    })
    .refine((date_of_birth) => validateMaxAge({
      date: date_of_birth,
      maxAge: MAX_CONTRACT_AGE,
      mode: 'INCLUSIVE',
      daysOffset: 1,
    }), {
      message: `Supera la edad máxima de contratación de ${MAX_CONTRACT_AGE} años`,
    }),
  gender: z.string().min(1, 'Este campo es requerido'),
  savings_option: z.enum([SavingsOption.savings_capacity, SavingsOption.goal_value]),
  monthly_savings: z.number(),
  goal_amount: z.number(),
  time_frame: z.number(),
  time_frame_as_option: selectNumberOption().nullable(),
  rentability_rate: z.preprocess(
    (amount) => Number(amount as string),
    z.number()
      .min(MIN_RENTABILITY_RATE, `El valor no debe ser menor a ${formatPercentage(MIN_RENTABILITY_RATE)}`)
      .max(MAX_RENTABILITY_RATE, `El valor no debe ser mayor a ${formatPercentage(MAX_RENTABILITY_RATE)}`),
  ),
  compensation_plan: z.string().min(1, 'Este campo es requerido'),
  death_insured_capital: z.preprocess(
    (amount) => Number(amount as string),
    z.number({ required_error: VALIDATION_MESSAGES.REQUIRED_FIELD })
      .min(minimumDeathCapital, `El capital mínimo es de ${minimumDeathCapital} UF`)
  ),
  extra_compensation_insured: z.object({
    itp: z.boolean(),
    severe_disease: z.boolean(),
  }),

  severe_diesase_assurance_rate: z.object({
    label: z.string(),
    value: z.number(),
  })
    .nullable(),
})
  .refine(({
    savings_option,
    monthly_savings,
  }) => validateSavingsOption(savings_option, SavingsOption.savings_capacity, monthly_savings),
  {
    message: `El valor objetivo no debe ser menor a ${(MIN_AMOUNT)} UF`,
    path: ['monthly_savings'],
  })
  .refine(({
    savings_option,
    time_frame,
  }) => validateTimeFrameForGoal(savings_option, time_frame, MIN_TIME_FRAME),
  {
    message: `El tiempo estimado de la meta no debe ser menor a ${MIN_TIME_FRAME} mes`,
    path: ['time_frame'],
  })
  .refine(({
    goal_amount,
    savings_option,
  }) => validateGoalAmountBySavingsOption(savings_option, goal_amount, MIN_AMOUNT), {
    message: `El valor objetivo no debe ser menor a ${(MIN_AMOUNT)} UF`,
    path: ['goal_amount'],
  });

export type FullFlexibleSimulationValues = z.infer<ReturnType<typeof fullFlexibleValidationSchema>>;

interface FullFlexiblePayload extends BaseSimulationParams {
  agreed_premium: number
  compensation_plan: string
  date_of_birth: string
  death_insured_capital: number
  gender: string
  goal: number
  itp_insured_capital: number
  rentability: number
  rut: string
  savings_option: keyof typeof SavingsOption
  serious_illness_insured_capital: number
  timeframe: number
}

export function useFullFlexible() {
  const mapFormToPayload = (values: FullFlexibleSimulationValues) => {
    const {
      rut,
      compensation_plan,
      date_of_birth,
      death_insured_capital,
      extra_compensation_insured,
      gender,
      goal_amount,
      rentability_rate,
      severe_diesase_assurance_rate,
      time_frame,
      time_frame_as_option,
      monthly_savings,
      savings_option,
    } = values;

    const itpCapital = extra_compensation_insured.itp ? death_insured_capital : 0;
    const seriousIllnessCapital = extra_compensation_insured.severe_disease
      ? Math.ceil(death_insured_capital * (severe_diesase_assurance_rate!.value / 100)) : 0;

    const timeframeOptionAsMonths = Number(time_frame_as_option?.value) * 12;
    const timeframe = savings_option === 'savings_capacity' ? timeframeOptionAsMonths : time_frame;

    const payload: FullFlexiblePayload = {
      rut,
      date_of_birth,
      gender,
      timeframe,
      compensation_plan,
      death_insured_capital,
      itp_insured_capital: itpCapital,
      serious_illness_insured_capital: seriousIllnessCapital,
      agreed_premium: monthly_savings,
      savings_option,
      goal: goal_amount,
      rentability: rentability_rate / 100,
      simulation_type: 'FULL_FLEXIBLE',
      wording: 'CLIENT',
    };

    return payload;
  };

  const handleSubmitSimulation = async (values: FullFlexibleSimulationValues) => {
    try {
      const payload = mapFormToPayload(values);

      simulationManager.render<FullFlexiblePayload>({
        srcOptions: {
          parameters: payload,
        },
        iframeAttributes: {
          classNames: ['full-flexible'],
          title: 'full-flexible-simulation',
        },
        parentElement: document.querySelector<HTMLDivElement>('#simulation-container')!,
      });

      await injectSimulationIdToInfograph(payload, 'FULL_FLEXIBLE');

      tag({
        event: EVENTS.SUBMIT,
        variables: {
          id: 'create_lead_form_fullflex',
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const renderEmptySimulation = () => {
    simulationManager.render<BaseSimulationParams>({
      srcOptions: {
        parameters: {
          wording: 'CLIENT',
          simulation_type: 'FULL_FLEXIBLE',
          is_empty: true,
        },
      },
      iframeAttributes: {
        classNames: ['full-flexible', 'empty'],
        title: 'full-flexible-simulation',
      },
      parentElement: document.querySelector<HTMLDivElement>('#simulation-container')!,
    });
  };

  useEffect(() => {
    renderEmptySimulation();
  }, []);

  return { handleSubmitSimulation };
}
